import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IRowData, ITableResponse } from "@/src/types/applicationOnline";
import { onlineApplicationsDefaultConfiguration, onlineApplicationAllFieldsList } from "@/src/constants/applicationOnlineConsts";
import { getUserConfigFromStorage } from "@/src/utils/userConfigStorage";

interface ColumnConfig {
  title: string;
  id: string;
  width?: number;
}

interface ApplicationsTableState {
  visibleColumns: ColumnConfig[];
  hiddenColumns: ColumnConfig[];
  data: IRowData[];
  selectedRows: number[];
}

const userConfig = getUserConfigFromStorage();

const initialState: ApplicationsTableState = {
  visibleColumns: userConfig?.AOPConfig?.AOPTable || onlineApplicationsDefaultConfiguration,
  hiddenColumns: onlineApplicationAllFieldsList.filter((col) => !onlineApplicationsDefaultConfiguration.some((def) => def.id === col.id)),
  data: [],
  selectedRows: [],
};

const applicationsTableSlice = createSlice({
  name: "applicationsTable",
  initialState,
  reducers: {
    setData(state, action: PayloadAction<ITableResponse>) {
      state.data = action.payload.data;
    },
    toggleColumnVisibility(state, action: PayloadAction<string>) {
      const colId = action.payload;
      const isVisible = state.visibleColumns.some((col) => col.id === colId);
      if (isVisible) {
        const col = state.visibleColumns.find((col) => col.id === colId)!;
        state.visibleColumns = state.visibleColumns.filter((col) => col.id !== colId);
        state.hiddenColumns.push({ title: col.title, id: col.id });
      } else {
        let col = state.hiddenColumns.find((col) => col.id === colId);
        if (!col) {
          col = onlineApplicationAllFieldsList.find((c) => c.id === colId)!;
        }
        state.hiddenColumns = state.hiddenColumns.filter((c) => c.id !== colId);
        state.visibleColumns.push({ title: col.title, id: col.id, width: 4 });
      }
    },
    setColumnWidth(state, action: PayloadAction<{ id: string; width: number }>) {
      const { id, width } = action.payload;
      const col = state.visibleColumns.find((c) => c.id === id);
      if (col) col.width = width;
    },
    toggleRowSelection(state, action: PayloadAction<number>) {
      const id = action.payload;
      if (state.selectedRows.includes(id)) {
        state.selectedRows = state.selectedRows.filter((rowId) => rowId !== id);
      } else {
        state.selectedRows.push(id);
      }
    },
    toggleAllRows(state) {
      if (state.selectedRows.length === state.data.length) {
        state.selectedRows = [];
      } else {
        state.selectedRows = state.data.map((row) => row.applyInfo.id);
      }
    },
    setActualFieldValue(state, action: PayloadAction<{ id: number; fieldActualValue: string; keyField: string }>) {
      const { id, fieldActualValue, keyField } = action.payload;
      const row = state.data.find((r) => r.applyInfo.id === id);
      if (!row) return;

      switch (keyField) {
        case "color_star":
          row.applyInfo.color_star = fieldActualValue;
          console.log(`Star color changed for ${id}: ${fieldActualValue}`);
          break;

        case "form_status":
          row.applyInfo.status = fieldActualValue;
          console.log(`Status changed for ${id}: ${fieldActualValue}`);
          break;

        default:
          console.warn(`Unknown keyField: ${keyField}`);
          break;
      }
    },
    showAllColumns(state) {
      state.visibleColumns = [...onlineApplicationAllFieldsList];
      state.hiddenColumns = [];
    },

    hideAllColumns(state) {
      state.hiddenColumns = [...onlineApplicationAllFieldsList];
      state.visibleColumns = [];
    },
    setVisibleColumns(state, action: PayloadAction<ColumnConfig[]>) {
      state.visibleColumns = action.payload;
      // Обновляем hiddenColumns на основе видимых колонок
      state.hiddenColumns = onlineApplicationAllFieldsList.filter((col) => !action.payload.some((visibleCol) => visibleCol.id === col.id));
    },
  },
});

export const {
  setData,
  toggleColumnVisibility,
  setColumnWidth,
  toggleRowSelection,
  toggleAllRows,
  setActualFieldValue,
  // setStatus,
  showAllColumns,
  hideAllColumns,
  setVisibleColumns,
} = applicationsTableSlice.actions;

export default applicationsTableSlice.reducer;
